import type { PumpTornadoChartContext } from "../../d";
import { getSimplifiedProducedTdh } from ".";
import type { OperatingPointDataSet } from "@/components/types";
import { getDensityCorrectedProducedPoints } from "@/libs/charting/_common/densityCorrection";
import { convert } from "@/libs/units";

export const getOperatingPointsTdh = (
  context: PumpTornadoChartContext,
  densityCorrectionRatio: number = 1
): OperatingPointDataSet => {
  const {
    producedTotalDynamicHeadFt,
    pumpIntakeFlowRateBBLD,
    pumpDischargeFlowRateBBLD,
    scenarioCalculations,
    gasHeadRerateRatio,
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseTdhYUnit,
    targetTdhYUnit,
    targetTdhYUnitLabel,
    pumpWearHeadCorrectionRatio,
    ascendingOrderPumps,
    requiredTotalDynamicHeadFt,
    requiredDifferentialPressurePSIA,
    isScenarioEspShore,
    dischargeFluidDensityLBMFT3,
    frequencyRatio
  } = context;

  const intakeProducedTdhData = getSimplifiedProducedTdh(
    pumpIntakeFlowRateBBLD,
    frequencyRatio,
    ascendingOrderPumps,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    dischargeFluidDensityLBMFT3
  );
  const intakeProduced = isScenarioEspShore
    ? intakeProducedTdhData.tdh
    : intakeProducedTdhData.tdhPressure;
  const intakePoint = {
    x: pumpIntakeFlowRateBBLD,
    y: intakeProduced
  };

  const dischargeProducedTdhData = getSimplifiedProducedTdh(
    pumpDischargeFlowRateBBLD,
    frequencyRatio,
    ascendingOrderPumps,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    dischargeFluidDensityLBMFT3
  );
  const dischargeProduced = isScenarioEspShore
    ? dischargeProducedTdhData.tdh
    : dischargeProducedTdhData.tdhPressure;
  const dischargePoint = {
    x: pumpDischargeFlowRateBBLD,
    y: dischargeProduced
  };

  // Correct the intake/discharge points for density so they intersect with
  // the frequency line
  const [correctedDischargePoint, correctedIntakePoint] =
    getDensityCorrectedProducedPoints(
      [dischargePoint, intakePoint],
      densityCorrectionRatio
    );

  const intakeProducedValue = Math.max(Number(correctedIntakePoint.y), 0);
  const correctedIntake = {
    x: correctedIntakePoint.x,
    y: intakeProducedValue
  };
  const dischargeProducedValue = Math.max(Number(correctedDischargePoint.y), 0);
  const correctedDischarge = {
    x: correctedDischargePoint.x,
    y: dischargeProducedValue
  };

  // Once we have the simplified and correct intake/discharge points, we can calculate
  // an average point to plot as the third operating point
  let averageFlowRateBBLD = 0;
  let averagePointY = isScenarioEspShore
    ? requiredTotalDynamicHeadFt
    : requiredDifferentialPressurePSIA;

  const perPumpAverageFlowRatesBBLD = ascendingOrderPumps.reduce<number[]>(
    (averages, pump) => {
      const averageStageFlowRateBBLD =
        scenarioCalculations.pumpCalculations.individual.value[pump.id]
          ?.averageStageFlowRateBBLD;

      if (typeof averageStageFlowRateBBLD === "number") {
        averages.push(averageStageFlowRateBBLD);
      }

      return averages;
    },
    []
  );
  averageFlowRateBBLD =
    perPumpAverageFlowRatesBBLD.length > 0
      ? perPumpAverageFlowRatesBBLD.reduce((sum, value) => sum + value, 0) /
        perPumpAverageFlowRatesBBLD.length
      : correctedDischargePoint.x;

  averagePointY = isScenarioEspShore
    ? getSimplifiedProducedTdh(
        averageFlowRateBBLD,
        frequencyRatio,
        ascendingOrderPumps,
        gasHeadRerateRatio,
        pumpWearHeadCorrectionRatio,
        dischargeFluidDensityLBMFT3
      ).tdh * densityCorrectionRatio
    : getSimplifiedProducedTdh(
        averageFlowRateBBLD,
        frequencyRatio,
        ascendingOrderPumps,
        gasHeadRerateRatio,
        pumpWearHeadCorrectionRatio,
        dischargeFluidDensityLBMFT3
      ).tdhPressure * densityCorrectionRatio;

  const averagePoint = {
    x: averageFlowRateBBLD,
    y: averagePointY
  };
  return isFinite(producedTotalDynamicHeadFt)
    ? {
        operatingPoints: [
          {
            label: `Discharge`,
            color: "var(--plotColor2)",
            rawPoint: correctedDischarge,
            point: {
              ...correctedDischarge,
              x: convert(correctedDischarge.x, baseXUnit, targetXUnit),
              y: convert(correctedDischarge.y, baseTdhYUnit, targetTdhYUnit)
            },
            xLabel: targetXUnitLabel,
            yLabel: targetTdhYUnitLabel
          },
          {
            label: "Average",
            color: "var(--plotColor3)",
            rawPoint: averagePoint,
            point: {
              ...averagePoint,
              x: convert(averagePoint.x, baseXUnit, targetXUnit),
              y: convert(averagePoint.y, baseTdhYUnit, targetTdhYUnit)
            },
            xLabel: targetXUnitLabel,
            yLabel: targetTdhYUnitLabel
          },
          {
            label: `Intake`,
            color: "var(--plotColor0)",
            rawPoint: correctedIntake,
            point: {
              ...correctedIntake,
              x: convert(correctedIntake.x, baseXUnit, targetXUnit),
              y: convert(correctedIntake.y, baseTdhYUnit, targetTdhYUnit)
            },
            xLabel: targetXUnitLabel,
            yLabel: targetTdhYUnitLabel
          }
        ]
      }
    : { operatingPoints: [] };
};
